// eslint-disable-next-line jira/restricted/graphql-tag
import gql from 'graphql-tag';
import { fg } from '@atlassian/jira-feature-gating';
import {
	LAYOUT_FRAGMENT,
	LAYOUT_FRAGMENT_WITH_ML,
	BASE_FIELD_FRAGMENT,
	PROJECT_FIELD_FRAGMENT_WITHOUT_CLASSIFICATION,
	ISSUE_TYPE_FIELD_FRAGMENT,
	ISSUE_TYPE_FIELD_FRAGMENT_WITH_HIERARCHY,
	REQUEST_TYPE_FIELD_FRAGMENT,
	TEXT_FIELD_FRAGMENT,
	RICH_TEXT_FIELD_FRAGMENT,
	COMPONENTS_FIELD_FRAGMENT,
	VERSION_FIELD_FRAGMENT,
	MULTI_VERSION_FIELD_FRAGMENT,
	PRIORITY_FIELD_FRAGMENT,
	SPRINT_FIELD_FRAGMENT,
	LABEL_FIELD_FRAGMENT,
	SINGLE_USER_PICKER_FIELD_FRAGMENT,
	MULTI_USER_PICKER_FIELD_FRAGMENT,
	PEOPLE_FIELD_FRAGMENT,
	SECURITY_FIELD_FRAGMENT,
	ATTACHMENTS_FIELD_FRAGMENT,
	ISSUE_RESTRICTION_FIELD_FRAGMENT,
	COLOR_FIELD_FRAGMENT,
	RESOLUTION_FIELD_FRAGMENT,
	SINGLE_SELECT_FIELD_FRAGMENT,
	MULTI_SELECT_FIELD_FRAGMENT,
	NUMBER_FIELD_FRAGMENT,
	URL_FIELD_FRAGMENT,
	EPIC_LINK_FIELD_FRAGMENT,
	DATE_FIELD_FRAGMENT,
	DATE_TIME_FIELD_FRAGMENT,
	CASCADING_SELECT_FIELD_FRAGMENT,
	CHECKBOX_FIELD_FRAGMENT,
	SINGLE_GROUP_PICKER_FIELD_FRAGMENT,
	MULTI_GROUP_PICKER_FIELD_FRAGMENT,
	TEAM_FIELD_FRAGMENT,
	ATLASSIAN_TEAM_FIELD_FRAGMENT,
	RADIO_SELECT_FIELD_FRAGMENT,
	AFFECTED_SERVICES_FIELD_FRAGMENT,
	CMDB_FIELD_FRAGMENT,
	ORGANIZATIONS_FIELD_FRAGMENT,
	CONNECT_TEXT_FIELD_FRAGMENT,
	CONNECT_NUMBER_FIELD_FRAGMENT,
	CONNECT_SINGLE_SELECT_FIELD_FRAGMENT,
	CONNECT_MULTI_SELECT_FIELD_FRAGMENT,
	CONNECT_RICH_TEXT_FIELD_FRAGMENT,
	ASSET_FIELD_FRAGMENT,
	FORGE_STRING_FIELD_FRAGMENT,
	FORGE_NUMBER_FIELD_FRAGMENT,
	FORGE_SINGLE_USER_PICKER_FIELD_FRAGMENT,
	FORGE_MULTI_USER_PICKER_FIELD_FRAGMENT,
	FORGE_GROUP_PICKER_FIELD_FRAGMENT,
	FORGE_MULTI_GROUP_PICKER_FIELD_FRAGMENT,
	FORGE_STRINGS_FIELD_FRAGMENT,
	FORGE_DATETIME_FIELD_FRAGMENT,
	FORGE_OBJECT_FIELD_FRAGMENT,
	TIME_TRACKING_FIELD_FRAGMENT,
	WORK_LOG_FIELD_FRAGMENT,
	JSM_MULTI_USER_PICKER_FIELD_FRAGMENT,
	JSM_PEOPLE_FIELD_FRAGMENT,
	PARENT_LINK_FIELD_FRAGMENT,
	PARENT_FIELD_FRAGMENT,
	PROFORMA_CONFIG_FRAGMENT,
	STATUS_FIELD_FRAGMENT,
	RESPONDERS_FIELD_FRAGMENT,
	WATCHERS_CONFIG_FRAGMENT,
	ISSUE_LINKS_FIELD_FRAGMENT_WITH_COPY_LINKS_AND_ATTACHMENTS_REFINED,
	ISSUE_LINKS_FIELD_FRAGMENT_REFINED,
	STORAGE_LIMITS_FRAGMENT,
} from '../../common/graphql/fragments.tsx';

// eslint-disable-next-line jira/ff/inline-usage
const isJsmRespondersFieldEnabled = () => fg('jsm-responders-field-in-gicx-ui');

export const getCreateConfigUserPreferredQueryInContext = (
	withML: boolean,
	isCreateLinkedIssue: boolean,
) => gql`
    query JiraGlobalIssueCreateInContext${isCreateLinkedIssue && fg('create_linked_issues_trigger_point_issue_view') ? 'WithCLI' : ''}(
        $project: JiraProjectInput
        $issueType: JiraIssueTypeInput
        $requestType: JiraRequestTypeInput
        $defaultValues: JiraIssueFieldsInput
		$withNumberFormat: Boolean!
        $additionalContext: JiraIssueCreateContextInput!
        ${fg('create_linked_issues_trigger_point_issue_view') ? '$issueCreateType: IssueCreateTypeFilter' : ''}
		$cloudId: ID!
		$applicationKey: JiraApplicationKey!
		$shouldCheckForStorageLimit: Boolean!
    ) {
        ${withML ? 'jiraIssueCreateLayoutInContextWithML' : 'jiraIssueCreateLayoutInContext'}(
            layoutFilter: USER_CONFIGURED_LAYOUT
            project: $project
            issueType: $issueType
            additionalContext: $additionalContext
            ${fg('create_linked_issues_trigger_point_issue_view') ? 'issueCreateType: $issueCreateType' : ''}
        ) {
            ...LayoutFragment
        }
        jiraIssueCreateFieldsInContext(
            fieldsFilter: USER_CONFIGURED_FIELDS
            project: $project
            issueType: $issueType
            additionalContext: $additionalContext
            requestType: $requestType
            defaultValues: $defaultValues
            ${fg('create_linked_issues_trigger_point_issue_view') ? 'issueCreateType: $issueCreateType' : ''}
        ) {
            ... on JiraIssueCreateFields {
                fields {
                    nodes {
                        ...BaseFieldFragment
                        ...ProjectFieldFragmentWithoutClassification
                        ${fg('add_issuehierarchy_attr_to_issue_created_event') ? '...IssueTypeFieldFragmentWithHierarchy' : '...IssueTypeFieldFragment'}
                        ...RequestTypeFieldFragment
                        ...TextFieldFragment
                        ...RichTextFieldFragment
                        ...ComponentsFieldFragment
                        ...VersionFieldFragment
                        ...MultiVersionFieldFragment
                        ...PriorityFieldFragment
                        ...SprintFieldFragment
                        ...LabelFieldFragment
                        ...SingleUserPickerFieldFragment
                        ...MultiUserPickerFieldFragment
                        ...PeopleFieldFragment
                        ...SecurityFieldFragment
                        ...IssueRestrictionFieldFragment
                        ...ColorFieldFragment
                        ...ResolutionFieldFragment
                        ...SingleSelectFieldFragment
                        ...MultiSelectFieldFragment
                        ...NumberFieldFragment
                        ...UrlFieldFragment
                        ...EpicLinkFieldFragment
                        ...DatePickerFieldFragment
                        ...DateTimePickerFieldFragment
                        ...CascadingSelectFieldFragment
                        ...AttachmentsFieldFragment
                        ...CheckboxFieldFragment
                        ${
													fg('create_linked_issues_trigger_point_issue_view')
														? '...IssueLinksFieldFragmentWithCopyLinksAndAttachmentsRefined'
														: '...IssueLinksFieldFragmentRefined'
												}
                        ...SingleGroupPickerFieldFragment
                        ...MultiGroupPickerFieldFragment
                        ...TeamFieldFragment
                        ...AtlassianTeamFieldFragment
                        ...RadioSelectFieldFragment
                        ...AffectedServicesFieldFragment
                        ...CMDBFieldFragment
                        ...OrganizationsFieldFragment
                        ...ConnectTextFieldFragment
                        ...ConnectNumberFieldFragment
                        ...ConnectSingleSelectFieldFragment
                        ...ConnectMultiSelectFieldFragment
                        ...ConnectRichTextFieldFragment
                        ...AssetFieldFragment
                        ...ForgeStringFieldFragment
                        ...ForgeNumberFieldFragment
                        ...ForgeSingleUserPickerFieldFragment
                        ...ForgeMultiUserPickerFieldFragment
                        ...ForgeGroupPickerFieldFragment
                        ...ForgeMultiGroupPickerFieldFragment
                        ...ForgeStringsFieldFragment
                        ...ForgeObjectFieldFragment
                        ...ForgeDateTimePickerFieldFragment
                        ...TimeTrackingFieldFragment
                        ...WorkLogFieldFragment
                        ...JsmMultiUserPickerFieldFragment
                        ...JsmPeopleFieldFragment
                        ...ParentLinkFieldFragment
                        ...ParentFieldFragment
                        ...StatusFieldFragment
                        ${
													isJsmRespondersFieldEnabled()
														? '...JiraServiceManagementRespondersFieldFragment'
														: ''
												}
                    }
                }
                errors {
                    identifier
                    extensions {
                        ... on JiraIssueFieldUnsupportedErrorExtension {
                            fieldName
                            fieldType
                            isRequiredField
                        }
                        ... on JiraIssueFieldValueValidationErrorExtension {
                            errorType
                            message
                        }
                    }
                }
            }
            ... on QueryError {
                message
            }
        }
        jiraIssueCreateAdditionalServices(project: $project, issueType: $issueType, requestType: $requestType) {
            ... on JiraIssueCreateAdditionalServices {
                services {
                    edges {
                        node {
                            ...WatchersConfigFragment
                            ...ProformaConfigFragment
                        }
                    }
                }
            }
            ... on QueryError {
                message
            }
        }
        jira @include(if: $shouldCheckForStorageLimit) {
            ...StorageLimitsFragment
        }
    }
    ${withML ? LAYOUT_FRAGMENT_WITH_ML : LAYOUT_FRAGMENT}
    ${BASE_FIELD_FRAGMENT}
    ${PROJECT_FIELD_FRAGMENT_WITHOUT_CLASSIFICATION}
    ${fg('add_issuehierarchy_attr_to_issue_created_event') ? ISSUE_TYPE_FIELD_FRAGMENT_WITH_HIERARCHY : ISSUE_TYPE_FIELD_FRAGMENT}
    ${REQUEST_TYPE_FIELD_FRAGMENT}
    ${TEXT_FIELD_FRAGMENT}
    ${RICH_TEXT_FIELD_FRAGMENT}
    ${COMPONENTS_FIELD_FRAGMENT}
    ${VERSION_FIELD_FRAGMENT}
    ${MULTI_VERSION_FIELD_FRAGMENT}
    ${PRIORITY_FIELD_FRAGMENT}
    ${SPRINT_FIELD_FRAGMENT}
    ${LABEL_FIELD_FRAGMENT}
    ${SINGLE_USER_PICKER_FIELD_FRAGMENT}
    ${MULTI_USER_PICKER_FIELD_FRAGMENT}
    ${SECURITY_FIELD_FRAGMENT}
    ${ISSUE_RESTRICTION_FIELD_FRAGMENT}
    ${COLOR_FIELD_FRAGMENT}
    ${RESOLUTION_FIELD_FRAGMENT}
    ${SINGLE_SELECT_FIELD_FRAGMENT}
    ${MULTI_SELECT_FIELD_FRAGMENT}
    ${NUMBER_FIELD_FRAGMENT}
    ${URL_FIELD_FRAGMENT}
    ${EPIC_LINK_FIELD_FRAGMENT}
    ${DATE_FIELD_FRAGMENT}
    ${DATE_TIME_FIELD_FRAGMENT}
    ${CASCADING_SELECT_FIELD_FRAGMENT}
    ${ATTACHMENTS_FIELD_FRAGMENT}
    ${CHECKBOX_FIELD_FRAGMENT}
    ${
			fg('create_linked_issues_trigger_point_issue_view')
				? ISSUE_LINKS_FIELD_FRAGMENT_WITH_COPY_LINKS_AND_ATTACHMENTS_REFINED
				: ISSUE_LINKS_FIELD_FRAGMENT_REFINED
		}
    ${SINGLE_GROUP_PICKER_FIELD_FRAGMENT}
    ${MULTI_GROUP_PICKER_FIELD_FRAGMENT}
    ${PEOPLE_FIELD_FRAGMENT}
    ${TEAM_FIELD_FRAGMENT}
    ${ATLASSIAN_TEAM_FIELD_FRAGMENT}
    ${RADIO_SELECT_FIELD_FRAGMENT}
    ${AFFECTED_SERVICES_FIELD_FRAGMENT}
    ${CMDB_FIELD_FRAGMENT}
    ${ORGANIZATIONS_FIELD_FRAGMENT}
    ${CONNECT_TEXT_FIELD_FRAGMENT}
    ${CONNECT_NUMBER_FIELD_FRAGMENT}
    ${CONNECT_SINGLE_SELECT_FIELD_FRAGMENT}
    ${CONNECT_MULTI_SELECT_FIELD_FRAGMENT}
    ${CONNECT_RICH_TEXT_FIELD_FRAGMENT}
    ${ASSET_FIELD_FRAGMENT}
    ${FORGE_STRING_FIELD_FRAGMENT}
    ${FORGE_NUMBER_FIELD_FRAGMENT}
    ${FORGE_SINGLE_USER_PICKER_FIELD_FRAGMENT}
    ${FORGE_MULTI_USER_PICKER_FIELD_FRAGMENT}
    ${FORGE_GROUP_PICKER_FIELD_FRAGMENT}
    ${FORGE_MULTI_GROUP_PICKER_FIELD_FRAGMENT}
    ${FORGE_STRINGS_FIELD_FRAGMENT}
    ${FORGE_DATETIME_FIELD_FRAGMENT}
    ${FORGE_OBJECT_FIELD_FRAGMENT}
    ${TIME_TRACKING_FIELD_FRAGMENT}
    ${WORK_LOG_FIELD_FRAGMENT}
    ${JSM_MULTI_USER_PICKER_FIELD_FRAGMENT}
    ${JSM_PEOPLE_FIELD_FRAGMENT}
    ${PARENT_LINK_FIELD_FRAGMENT}
    ${PARENT_FIELD_FRAGMENT}
    ${PROFORMA_CONFIG_FRAGMENT}
    ${STATUS_FIELD_FRAGMENT}
    ${isJsmRespondersFieldEnabled() ? RESPONDERS_FIELD_FRAGMENT : ''}
    ${WATCHERS_CONFIG_FRAGMENT}
    ${STORAGE_LIMITS_FRAGMENT}
`;
